import type { DocumentDTO } from "@zplanem/responses"
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { ApiError } from "../../../lib/api-client"
import {
	useGrantCandidates,
	useGrantDocumentAccess,
	useRevokeDocumentAccess,
} from "../../hooks/use-documents"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"

export function DocumentGrantsModal({
	propertyId,
	document,
	onClose,
}: {
	propertyId: string
	document: DocumentDTO
	onClose: () => void
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const td = t.projects.documents
	const candidates = useGrantCandidates(propertyId, document.id)
	const grant = useGrantDocumentAccess(propertyId, document.id)
	const revoke = useRevokeDocumentAccess(propertyId, document.id)
	const [actionError, setActionError] = useState<string | null>(null)

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose()
		}
		window.addEventListener("keydown", handler)
		return () => window.removeEventListener("keydown", handler)
	}, [onClose])

	async function toggle(userId: string, currentlyGranted: boolean) {
		setActionError(null)
		try {
			if (currentlyGranted) {
				await revoke.mutateAsync(userId)
			} else {
				await grant.mutateAsync(userId)
			}
		} catch (err) {
			setActionError(err instanceof ApiError ? err.message : td.errorLoad)
		}
	}

	const pending = grant.isPending || revoke.isPending

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-labelledby="doc-grants-title"
			className="fixed inset-0 z-50 flex items-center justify-center px-4"
		>
			<button
				type="button"
				aria-label={td.cancel}
				className="absolute inset-0 bg-black/60"
				onClick={onClose}
			/>
			<div className="relative w-full max-w-md rounded-lg border border-border bg-popover text-popover-foreground p-6 shadow-2xl">
				<div className="flex items-start justify-between">
					<h2
						id="doc-grants-title"
						className="text-xl font-bold text-foreground"
					>
						{td.grantsModalTitle}
					</h2>
					<button
						type="button"
						onClick={onClose}
						aria-label={td.cancel}
						className="text-muted-foreground hover:text-foreground px-2 py-1"
					>
						×
					</button>
				</div>
				<p className="mt-1 text-sm text-muted-foreground truncate">
					{document.title}
				</p>

				<div className="mt-4 space-y-1 max-h-72 overflow-y-auto">
					{candidates.isLoading ? (
						<p className="text-sm text-muted-foreground">…</p>
					) : (
						(candidates.data ?? []).map((candidate) => (
							<div
								key={candidate.userId}
								className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-muted/40"
							>
								<span className="text-sm text-foreground truncate">
									{candidate.displayName}
								</span>
								{candidate.isOwner ? (
									<span className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[color:var(--ring)] shrink-0">
										{td.ownerLabel}
									</span>
								) : (
									<button
										type="button"
										disabled={pending}
										onClick={() =>
											void toggle(candidate.userId, candidate.granted)
										}
										className={
											"shrink-0 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 " +
											(candidate.granted
												? "bg-primary/10 text-primary hover:bg-destructive/10 hover:text-destructive"
												: "bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary")
										}
									>
										{candidate.granted
											? td.grantToggleRevoke
											: td.grantToggleGrant}
									</button>
								)}
							</div>
						))
					)}
				</div>

				{actionError ? (
					<div
						role="alert"
						className="mt-4 rounded-xl border border-destructive/30 bg-destructive/10 text-destructive text-sm px-4 py-3"
					>
						{actionError}
					</div>
				) : null}

				<div className="mt-5 flex justify-end">
					<Button variant="ghost" onClick={onClose}>
						{td.cancel}
					</Button>
				</div>
			</div>
		</div>
	)
}
